import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, MenuItem, Select, TextField, Tooltip, Typography } from "@mui/material";
import { DataGrid, GridFooterContainer } from "@mui/x-data-grid";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { format } from "date-fns";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import CloseIcon from "@mui/icons-material/Close";
import HighlightOffOutlinedIcon from "@mui/icons-material/HighlightOffOutlined";
import NorthEastIcon from "@mui/icons-material/NorthEast";
import SouthWestIcon from "@mui/icons-material/SouthWest";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import Pagination from "pages/homeDashboard/components/Pagination/Pagination";
import { useGetActiveOrganizationsQuery } from "state/rtkQuery/org/orgQueries";
import { useGetPlatformsQuery } from "state/rtkQuery/platform/platformQueries";
import { useReviewBudgetRequestsMutation } from "state/rtkQuery/user/userMutations";
import { useGetBudgetRequestsQuery, useGetCostCenterUsersQuery, useLazyGetUserQuery } from "state/rtkQuery/user/userQueries";
import { showToast } from "utils/reusableToast";
import { useDebounce } from "utils/useDebounce";

import ReasonColumn from "../ReasonColumn/ReasonColumn";
import TableRowsSkeleton from "../TableRowsSkeleton/TableRowsSkeleton";
import UserDetailsModal from "../UserDetailsModal/UserDetailsModal";

import "../AdminDataGrid/AdminDataGrid.css";

const RECORDS_PER_PAGE = 20;

const ACCENT = "#26890d";
const USAGE_RED = "#E2231A";
const USAGE_ORANGE = "#F28C00";

const ACTION_OPTIONS = [
  { value: "all", label: "All" },
  { value: "increase", label: "Increase" },
  { value: "decrease", label: "Decrease" },
  { value: "admin_set", label: "Admin Override" },
];

// "Filter By" field selector — mirrors the pattern used by the OrgHistory
// tabs' own combined filter dropdown (see useTabSortFilter.js): an empty
// value means "search by email" (the default), any other value swaps the
// search box for a field-specific input (an Action dropdown or a Request
// Date picker). Status is deliberately NOT included here — it's the "which
// bucket am I viewing" filter this whole page defaults around (Pending
// Approvals = status=pending), the same role JoinedTab's own membership
// status plays outside of ITS Filter By dropdown.
const SEARCH_FILTER_OPTIONS = [
  { value: "action", label: "Action" },
  { value: "request_date", label: "Request Date" },
];

const STATUS_OPTIONS = [
  { value: "pending", label: "Pending" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
  { value: "all", label: "All" },
];

const formatUsd = (v) =>
  "$" + Number(v ?? 0).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const formatDate = (isoDate) =>
  isoDate ? new Date(isoDate).toLocaleDateString("en-US") : "";

// GitHub Copilot budgets come in three scopes (see
// https://docs.github.com/en/copilot/concepts/billing/budgets-for-usage-based-billing):
// a per-user ULB, a cost-center budget shared by everyone in that cost
// center, or the universal (enterprise-wide) budget that applies when
// neither of the above is set. `current_budget.budget_scope` on a request
// tells us which one is *currently effective* for that user — only one ever
// applies at a time, in that priority order (ULB > cost center > universal).
// `budget_scope: "user"` means they already have their own ULB, so approving
// just adjusts it. Any other scope means they don't yet — approving will
// create a brand-new ULB for them, split off from whichever shared budget
// is currently covering them.
const isSharedScope = (scope) => Boolean(scope) && scope !== "user";

// request_id should always be present per the API contract, but some rows
// have been observed coming back without it — falling back to a composite
// key keeps the DataGrid from crashing outright (it requires every row to
// have a unique id) while that gap gets fixed server-side. user_id alone is
// NOT unique, since a user can submit multiple requests.
const getRequestRowId = (row) =>
  row.request_id ?? `${row.user_id}-${row.request_date}-${row.action}`;

const SCOPE_LABELS = {
  user: "ULB",
  multi_user_cost_center: "Cost Center Level Budget",
  multi_user_customer: "Universal Budget",
};

const scopeLabel = (scope) => SCOPE_LABELS[scope] ?? (scope ? "Shared Budget" : "—");

const scopeAppliedLabel = (scope) => `${scopeLabel(scope)} Applied`;

// Color-codes the three budget scopes so an org owner can tell at a glance
// which one is currently effective for a user — one ULB color (their own,
// individually-managed budget) vs two shades for the shared fallbacks.
const SCOPE_CHIP_STYLES = {
  user: { color: ACCENT, bg: "rgba(38,137,13,0.1)" },
  multi_user_cost_center: { color: "#0b5fa5", bg: "rgba(11,95,165,0.1)" },
  multi_user_customer: { color: "#6a4fb3", bg: "rgba(106,79,179,0.1)" },
};

const BudgetScopeChip = ({ scope }) => {
  const { color, bg } = SCOPE_CHIP_STYLES[scope] ?? { color: "#666", bg: "rgba(0,0,0,0.06)" };
  return (
    <Chip
      label={scopeAppliedLabel(scope)}
      size="small"
      sx={{ color, backgroundColor: bg, fontWeight: 700, fontSize: "0.62rem", height: "18px" }}
    />
  );
};

const StatusChip = ({ status }) => {
  const map = {
    pending: { label: "Pending", color: "#F28C00", bg: "rgba(242,140,0,0.1)" },
    approved: { label: "Approved", color: ACCENT, bg: "rgba(38,137,13,0.1)" },
    rejected: { label: "Rejected", color: USAGE_RED, bg: "rgba(226,35,26,0.1)" },
  };
  const { label, color, bg } = map[status] ?? map.pending;
  return <Chip label={label} size="small" sx={{ color, backgroundColor: bg, fontWeight: 700, fontSize: "0.72rem" }} />;
};

const REJECT_REASON_OPTIONS = [
  "Insufficient business justification",
  "Amount exceeds what's needed",
  "Other",
];

// Extracted so PendingApprovalsTabs.jsx can render these on the same line as
// its search box (in its own header row) instead of inside this table. Also
// owns the "Filter By" field selector + its dynamic search input (email text
// box vs. request-date picker) — folding the search box in here means one
// place decides what's currently being searched by, instead of the caller
// needing to know about searchField at all.
export const UlbRequestFilters = ({
  statusFilter,
  onStatusFilterChange,
  searchField,
  onSearchFieldChange,
  searchQuery,
  onSearchQueryChange,
}) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
      <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: "#333", whiteSpace: "nowrap" }}>
        Status
      </Typography>
      <Select
        size="small"
        value={statusFilter}
        onChange={(e) => onStatusFilterChange(e.target.value)}
        sx={{ backgroundColor: "#fff", fontSize: "0.82rem", minWidth: 140 }}
      >
        {STATUS_OPTIONS.map((opt) => (
          <MenuItem key={opt.value} value={opt.value}>
            {opt.label}
          </MenuItem>
        ))}
      </Select>
    </Box>
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
      <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: "#333", whiteSpace: "nowrap" }}>
        Filter By
      </Typography>
      <Select
        size="small"
        displayEmpty
        value={searchField}
        onChange={(e) => onSearchFieldChange(e.target.value)}
        sx={{ backgroundColor: "#fff", fontSize: "0.82rem", minWidth: 140 }}
      >
        <MenuItem value="">Email</MenuItem>
        {SEARCH_FILTER_OPTIONS.map((opt) => (
          <MenuItem key={opt.value} value={opt.value}>
            {opt.label}
          </MenuItem>
        ))}
      </Select>
    </Box>
    {searchField === "action" ? (
      <Select
        size="small"
        displayEmpty
        value={searchQuery || ""}
        onChange={(e) => onSearchQueryChange(e.target.value)}
        renderValue={(value) => (value ? ACTION_OPTIONS.find((o) => o.value === value)?.label : <span style={{ color: "#999" }}>Select an action</span>)}
        sx={{ backgroundColor: "#fff", fontSize: "0.82rem", minWidth: 180 }}
      >
        {ACTION_OPTIONS.filter((opt) => opt.value !== "all").map((opt) => (
          <MenuItem key={opt.value} value={opt.value}>
            {opt.label}
          </MenuItem>
        ))}
      </Select>
    ) : searchField === "request_date" ? (
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <DatePicker
          value={searchQuery || null}
          onChange={(newValue) => onSearchQueryChange(newValue)}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              placeholder="Select Date"
              sx={{ backgroundColor: "#fff", minWidth: 180 }}
              InputProps={{ ...params.InputProps, style: { height: "37px", fontSize: "0.82rem" } }}
            />
          )}
        />
      </LocalizationProvider>
    ) : (
      <TextField
        size="small"
        placeholder="Search by Email"
        type="search"
        value={searchQuery}
        onChange={(e) => onSearchQueryChange(e.target.value)}
        sx={{ backgroundColor: "#fff", minWidth: 200, "& .MuiInputBase-input": { fontSize: "0.82rem" } }}
      />
    )}
  </Box>
);

// statusFilter/searchField/searchQuery/sortBy/sortOrder and their setters
// are optionally controlled by the caller (see PendingApprovalsTabs.jsx,
// which renders <UlbRequestFilters> in its own header row) — each falls
// back to internal state so this component still works standalone. Action
// is no longer its own independent filter — it's one of the fields the
// "Filter By" selector can target (searchField === "action"), same as
// Email and Request Date, so there's no separate actionFilter prop anymore.
const UlbRequestsTable = ({
  searchQuery: searchQueryProp,
  onSearchQueryChange,
  statusFilter: statusFilterProp,
  onStatusFilterChange,
  searchField: searchFieldProp,
  onSearchFieldChange,
}) => {
  const userEmail = useSelector((state) => state.auth?.userAccount?.username);
  const { orgId } = useParams();
  const { data: platformData } = useGetPlatformsQuery();
  const platformId = platformData?._id;

  const [internalStatusFilter, setInternalStatusFilter] = useState("pending");
  const [internalSearchField, setInternalSearchField] = useState("");
  const [internalSearchQuery, setInternalSearchQuery] = useState("");
  const statusFilter = statusFilterProp ?? internalStatusFilter;
  const searchField = searchFieldProp ?? internalSearchField;
  const searchQuery = searchQueryProp ?? internalSearchQuery;
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");

  // request_date search comes from a DatePicker as a Date object (or a
  // YYYY-MM-DD string, when this component owns its own uncontrolled
  // state) — normalize either to the YYYY-MM-DD the API expects.
  const requestDateParam =
    searchField === "request_date"
      ? searchQuery instanceof Date && !isNaN(searchQuery)
        ? format(searchQuery, "yyyy-MM-dd")
        : typeof searchQuery === "string"
          ? searchQuery
          : ""
      : "";

  // When Filter By is set to Action, searchQuery holds the chosen action
  // value directly (from a dropdown, not free text) — no debouncing needed.
  const actionParam = searchField === "action" ? searchQuery || "all" : "all";

  // Debounced so a search isn't fired on every keystroke — matches the
  // pattern used by the OrgHistory tabs' own email search. Only the email
  // search is free text; the date/action fields only ever change via
  // picking a value, so debouncing them would just add a needless delay.
  const debouncedSearchQuery = useDebounce(searchQuery, 400);
  const emailParam = searchField === "" ? debouncedSearchQuery : "";

  // Three-state toggle (none → asc → desc → none), same as JoinedTab's own
  // sortable column headers — clicking the same field again flips
  // direction, a third click clears the sort entirely.
  const handleSort = useCallback(
    (field) => {
      if (sortBy === field) {
        if (sortOrder === "asc") {
          setSortOrder("desc");
        } else {
          setSortBy("");
          setSortOrder("asc");
        }
      } else {
        setSortBy(field);
        setSortOrder("asc");
      }
      setCurrentPage(1);
    },
    [sortBy, sortOrder]
  );

  const renderSortIcon = useCallback(
    (field) => (
      <Tooltip
        title={
          sortBy === field
            ? sortOrder === "asc"
              ? "Click for descending"
              : "Click to clear sort"
            : "Click to sort ascending"
        }
        placement="top"
      >
        <IconButton
          className="sort-icon"
          size="small"
          onClick={() => handleSort(field)}
          sx={{
            position: "absolute",
            right: 0,
            padding: "2px",
            minWidth: "28px",
            flexShrink: 0,
            opacity: 0,
            transition: "opacity 0.2s",
            color: sortBy === field ? "#1976d2" : "#666",
          }}
        >
          {sortBy === field && sortOrder === "desc" ? (
            <ArrowDownwardIcon fontSize="small" />
          ) : (
            <ArrowUpwardIcon fontSize="small" />
          )}
        </IconButton>
      </Tooltip>
    ),
    [sortBy, sortOrder, handleSort]
  );

  const {
    data: requestsData,
    isFetching: isTableLoading,
  } = useGetBudgetRequestsQuery(
    {
      orgId,
      platformId,
      action: actionParam,
      status: statusFilter,
      page: currentPage,
      perPage: RECORDS_PER_PAGE,
      email: emailParam,
      requestDate: requestDateParam,
      sortBy,
      sortOrder,
    },
    { skip: !orgId || !platformId, refetchOnMountOrArgChange: true }
  );

  const pageRequests = requestsData?.requests ?? [];
  const totalCount = requestsData?.total ?? 0;
  // Only show the skeleton on a genuine first load — once any data has
  // arrived, subsequent filter/page refetches use the DataGrid's own
  // (lighter) loading overlay instead of replacing the whole table.
  const isInitialLoad = isTableLoading && requestsData === undefined;

  const nPages = Math.max(1, Math.ceil(totalCount / RECORDS_PER_PAGE));

  // Org/cost-center name lookups and the "view user details" flow — mirrors
  // AdminDashboard's own Profile column exactly, so org owners get the same
  // user-detail popup here as on the "Pending Approvals" tab.
  const { data: allOrgs, isSuccess: isOrgsSuccess } = useGetActiveOrganizationsQuery(
    { platformId },
    { skip: !platformId }
  );
  const orgIdToName = useMemo(() => {
    if (!isOrgsSuccess || !Array.isArray(allOrgs)) return {};
    return allOrgs.reduce((acc, org) => {
      acc[org._id] = org.name;
      return acc;
    }, {});
  }, [allOrgs, isOrgsSuccess]);

  const { data: costCentersData, isSuccess: isCostCentersSuccess } = useGetCostCenterUsersQuery();
  const costCenterById = useMemo(() => {
    const costCenters = costCentersData?.cost_centers;
    if (!isCostCentersSuccess || !Array.isArray(costCenters)) return {};
    return costCenters.reduce((acc, cc) => {
      acc[cc._id] = cc.name;
      return acc;
    }, {});
  }, [costCentersData, isCostCentersSuccess]);

  const [triggerGetUser] = useLazyGetUserQuery();
  const [showUserDetailsModal, setShowUserDetailsModal] = useState(false);
  const [selectedUserDetails, setSelectedUserDetails] = useState(null);
  const [isUserDetailsLoading, setIsUserDetailsLoading] = useState(false);
  const [userDetailsError, setUserDetailsError] = useState("");

  const handleOpenUserDetails = async (row) => {
    setShowUserDetailsModal(true);
    setIsUserDetailsLoading(true);
    setUserDetailsError("");
    try {
      const result = await triggerGetUser({ email: row.email, platformId });
      const users = Array.isArray(result?.data) ? result.data : [];
      const exactMatch = users.find((user) => user._id === row.user_id);
      setSelectedUserDetails(exactMatch || users[0] || null);

      if (!users.length) {
        setUserDetailsError("Could not find user details for this request.");
      }
    } catch (error) {
      setSelectedUserDetails(null);
      setUserDetailsError("Failed to load user details. Please try again.");
    } finally {
      setIsUserDetailsLoading(false);
    }
  };

  const handleStatusFilterChange = (value) => {
    if (onStatusFilterChange) onStatusFilterChange(value);
    else setInternalStatusFilter(value);
  };

  const handleSearchFieldChange = (value) => {
    if (onSearchFieldChange) onSearchFieldChange(value);
    else setInternalSearchField(value);
  };

  const handleSearchQueryChange = (value) => {
    if (onSearchQueryChange) onSearchQueryChange(value);
    else setInternalSearchQuery(value);
  };

  const [reviewBudgetRequests] = useReviewBudgetRequestsMutation();

  const [selectedRowIds, setSelectedRowIds] = useState([]);

  // Resets pagination/selection whenever the *effective* filter/search values
  // change — whether the change came from this component's own fallback
  // filter row or from a controlling parent (e.g. PendingApprovalsTabs
  // rendering <UlbRequestFilters> and the search box in its own header,
  // bypassing the handlers above entirely). Skips the very first render so
  // mount doesn't reset anything.
  const isFirstFilterRender = useRef(true);
  useEffect(() => {
    if (isFirstFilterRender.current) {
      isFirstFilterRender.current = false;
      return;
    }
    setCurrentPage(1);
    setSelectedRowIds([]);
  }, [actionParam, statusFilter, searchField, emailParam, requestDateParam, sortBy, sortOrder]);

  const [reviewTarget, setReviewTarget] = useState(null); // { requests, decision }
  const [reviewOpen, setReviewOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [otherReason, setOtherReason] = useState("");
  const [approveReason, setApproveReason] = useState("");
  const [processing, setProcessing] = useState(false);

  // Single-row review (per-row Approve/Reject buttons) and bulk review
  // (toolbar buttons, ≥1 row selected) both open the same dialog — `requests`
  // is just a list either way, so a single-item array covers both.
  const openReview = (requests, decision) => {
    setReviewTarget({ requests, decision });
    setReviewOpen(true);
    setRejectReason("");
    setOtherReason("");
    setApproveReason("");
  };

  // Only flips `open` to false here — `reviewTarget` (which drives the
  // dialog's title/button color/label) is cleared in onExited below, once the
  // Dialog's fade-out transition finishes. Nulling it immediately caused the
  // Approve/Reject button to flash the wrong color for a frame while the
  // dialog was still fading out with reviewTarget already null.
  const closeReview = () => {
    if (processing) return;
    setReviewOpen(false);
  };

  const rejectReasonInvalid =
    reviewTarget?.decision === "reject" &&
    (rejectReason === "" || (rejectReason === "Other" && otherReason.trim() === ""));

  const confirmReview = async () => {
    if (!reviewTarget || rejectReasonInvalid) return;
    const { requests, decision } = reviewTarget;
    const finalRejectReason = rejectReason === "Other" ? otherReason.trim() : rejectReason;
    const apiDecision = decision === "approve" ? "approved" : "rejected";
    setProcessing(true);

    try {
      const result = await reviewBudgetRequests({
        platformId,
        orgId,
        reviewedBy: userEmail,
        actionReason: decision === "reject" ? finalRejectReason : approveReason.trim() || undefined,
        requests: requests.map((r) => ({ email: r.email, decision: apiDecision })),
      }).unwrap();

      const succeededCount = result?.succeeded_count ?? requests.length;
      const failedCount = result?.failed_count ?? 0;

      showToast({
        title: decision === "approve" ? "Requests approved" : "Requests rejected",
        description:
          failedCount > 0
            ? `${succeededCount} ${apiDecision}, ${failedCount} failed: ${result.failed
                .map((f) => `${f.email} (${f.error})`)
                .join("; ")}`
            : `${succeededCount} request${succeededCount === 1 ? "" : "s"} ${apiDecision}.`,
        type: failedCount > 0 ? "warning" : decision === "approve" ? "success" : "info",
        id: "ulb-review-toast",
      });
      setReviewOpen(false);
      setSelectedRowIds([]);
    } catch (error) {
      showToast({
        title: "Review failed",
        description: "We couldn't process this request. Please try again.",
        type: "error",
        id: "ulb-review-error-toast",
      });
    } finally {
      setProcessing(false);
    }
  };

  const columns = [
    {
      field: "email",
      headerName: "Email",
      flex: 1.3,
      minWidth: 180,
      sortable: false,
      renderHeader: () => (
        <Box
          sx={{
            display: "flex", alignItems: "center", gap: 0.25, overflow: "hidden",
            width: "100%", position: "relative", pr: 0,
            "&:hover": { pr: "30px" },
            "&:hover .sort-icon": { opacity: 1 },
          }}
        >
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontWeight: 700, fontSize: "14.5px" }}>
            Email
          </span>
          {renderSortIcon("email")}
        </Box>
      ),
      renderCell: (params) => (
        <Tooltip title={params.row.email}>
          <span className="table-cell-trucate-email">{params.row.email}</span>
        </Tooltip>
      ),
    },
    {
      field: "current_budget",
      headerName: "Current Budget",
      flex: 1.1,
      minWidth: 150,
      align: "center",
      headerAlign: "center",
      sortable: false,
      renderHeader: () => (
        <Box
          sx={{
            display: "flex", alignItems: "center", justifyContent: "center", gap: 0.25, overflow: "hidden",
            width: "100%", position: "relative", pr: 0,
            "&:hover": { pr: "30px" },
            "&:hover .sort-icon": { opacity: 1 },
          }}
        >
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontWeight: 700, fontSize: "14.5px" }}>
            Current Budget
          </span>
          {renderSortIcon("current_budget")}
        </Box>
      ),
      renderCell: (params) => {
        const scope = params.row.current_budget?.budget_scope;
        return (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.4 }}>
            <Typography sx={{ fontSize: "0.8rem", color: "#1a1a1a" }}>
              {formatUsd(params.row.current_budget?.budget_amount)}
            </Typography>
            <BudgetScopeChip scope={scope} />
          </Box>
        );
      },
    },
    {
      field: "change_amount",
      headerName: "Change Amount",
      flex: 1,
      minWidth: 140,
      align: "center",
      headerAlign: "center",
      sortable: false,
      renderHeader: () => (
        <Box
          sx={{
            display: "flex", alignItems: "center", justifyContent: "center", gap: 0.25, overflow: "hidden",
            width: "100%", position: "relative", pr: 0,
            "&:hover": { pr: "30px" },
            "&:hover .sort-icon": { opacity: 1 },
          }}
        >
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontWeight: 700, fontSize: "14.5px" }}>
            Change Amount
          </span>
          {renderSortIcon("change_amount")}
        </Box>
      ),
      renderCell: (params) => {
        const isIncrease = params.row.action === "increase";
        const isDecrease = params.row.action === "decrease";
        const color = isIncrease ? ACCENT : isDecrease ? USAGE_RED : "#1a1a1a";
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color }}>
            {isIncrease && <NorthEastIcon sx={{ fontSize: "1rem" }} />}
            {isDecrease && <SouthWestIcon sx={{ fontSize: "1rem" }} />}
            <Typography sx={{ fontSize: "0.83rem", fontWeight: 600, color }}>
              {formatUsd(params.row.change_amount)}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: "action",
      headerName: "Action",
      flex: 0.8,
      minWidth: 110,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        const label = ACTION_OPTIONS.find((o) => o.value === params.row.action)?.label ?? params.row.action;
        return <span style={{ fontSize: "0.8rem" }}>{label}</span>;
      },
    },
    {
      field: "status",
      headerName: "Status",
      flex: 0.7,
      minWidth: 100,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => <StatusChip status={params.row.status} />,
    },
    {
      field: "request_date",
      headerName: "Request Date",
      flex: 0.8,
      minWidth: 110,
      align: "center",
      headerAlign: "center",
      sortable: false,
      renderHeader: () => (
        <Box
          sx={{
            display: "flex", alignItems: "center", justifyContent: "center", gap: 0.25, overflow: "hidden",
            width: "100%", position: "relative", pr: 0,
            "&:hover": { pr: "30px" },
            "&:hover .sort-icon": { opacity: 1 },
          }}
        >
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontWeight: 700, fontSize: "14.5px" }}>
            Request Date
          </span>
          {renderSortIcon("request_date")}
        </Box>
      ),
      renderCell: (params) => <span style={{ fontSize: "12px" }}>{formatDate(params.row.request_date)}</span>,
    },
    {
      field: "request_reason",
      headerName: "Reason",
      flex: 1.6,
      minWidth: 220,
      cellClassName: "ulb-reason-cell-wrap",
      renderCell: (params) => <ReasonColumn params={{ row: { reason: params.row.request_reason } }} />,
    },
    {
      field: "profile",
      headerName: "Profile",
      flex: 0.5,
      minWidth: 70,
      align: "center",
      headerAlign: "center",
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Tooltip title="View user details">
          <IconButton
            size="small"
            aria-label="View user details"
            onClick={() => handleOpenUserDetails(params.row)}
            sx={{ p: 0.25, color: "#0b756c" }}
          >
            <InfoOutlinedIcon sx={{ color: "#0084ff", fontSize: 22 }} />
          </IconButton>
        </Tooltip>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.7,
      minWidth: 100,
      align: "center",
      headerAlign: "center",
      sortable: false,
      filterable: false,
      renderCell: (params) =>
        params.row.status === "pending" ? (
          <Box sx={{ position: "relative", display: "flex" }}>
            <Tooltip title="Approve">
              <CheckCircleOutlinedIcon
                style={{ color: "green", margin: "8px", cursor: "pointer" }}
                onClick={() => openReview([params.row], "approve")}
              />
            </Tooltip>
            <div style={{ margin: "4px 5px", fontSize: "20px" }}>|</div>
            <Tooltip title="Reject">
              <HighlightOffOutlinedIcon
                style={{ color: "red", margin: "8px 16px 8px 10px", cursor: "pointer" }}
                onClick={() => openReview([params.row], "reject")}
              />
            </Tooltip>
          </Box>
        ) : null,
    },
  ];

  const selectedRows = useMemo(
    () => pageRequests.filter((r) => selectedRowIds.includes(getRequestRowId(r))),
    [pageRequests, selectedRowIds]
  );

  const CustomFooter = () => (
    <GridFooterContainer
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        px: 2,
        py: 1,
        gap: 2,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, minHeight: 32 }}>
        {selectedRows.length > 0 && (
          <>
            <Button
              variant="contained"
              startIcon={<CheckCircleOutlinedIcon sx={{ fontSize: "1rem" }} />}
              sx={{
                backgroundColor: ACCENT,
                textTransform: "none",
                fontWeight: 600,
                px: 2,
                py: 0.5,
                borderRadius: "20px",
                fontSize: "0.78rem",
                boxShadow: "none",
                "&:hover": { backgroundColor: "#1e6b0a", boxShadow: "none" },
              }}
              onClick={() => openReview(selectedRows, "approve")}
            >
              Approve Selected ({selectedRows.length})
            </Button>
            <Button
              variant="contained"
              startIcon={<HighlightOffOutlinedIcon sx={{ fontSize: "1rem" }} />}
              sx={{
                backgroundColor: USAGE_RED,
                textTransform: "none",
                fontWeight: 600,
                px: 2,
                py: 0.5,
                borderRadius: "20px",
                fontSize: "0.78rem",
                boxShadow: "none",
                "&:hover": { backgroundColor: "#b81d15", boxShadow: "none" },
              }}
              onClick={() => openReview(selectedRows, "reject")}
            >
              Reject Selected ({selectedRows.length})
            </Button>
          </>
        )}
      </Box>
      <Pagination nPages={nPages} currentPage={currentPage} setCurrentPage={setCurrentPage} />
    </GridFooterContainer>
  );

  return (
    <Box sx={{ height: 600 }}>
      {/* When the caller controls these filters (passes onStatusFilterChange),
          it renders <UlbRequestFilters> itself elsewhere — e.g. on the same
          line as a search box — so this fallback row only appears when used
          standalone (uncontrolled). */}
      {!onStatusFilterChange && (
        <Box sx={{ display: "flex", alignItems: "center", mb: 1, justifyContent: "flex-end" }}>
          <UlbRequestFilters
            statusFilter={statusFilter}
            onStatusFilterChange={handleStatusFilterChange}
            searchField={searchField}
            onSearchFieldChange={handleSearchFieldChange}
            searchQuery={searchQuery}
            onSearchQueryChange={handleSearchQueryChange}
          />
        </Box>
      )}
      {isInitialLoad ? (
        <TableRowsSkeleton columns={columns} rowCount={3} rowHeight={55} />
      ) : (
        <DataGrid
          sx={{
            "& .MuiDataGrid-columnHeaderTitle": {
              fontSize: "14.5px !important",
              fontWeight: "bold !important",
            },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#f6f6f6",
              maxHeight: "45px",
            },
            "& .MuiDataGrid-columnHeadersInner": {
              backgroundColor: "#f6f6f6 !important",
            },
            "& .MuiDataGrid-row": {
              minHeight: "55px !important",
              boxShadow: "none !important",
              borderBottom: "0.5px solid #d4d4d4",
            },
            "& .MuiDataGrid-virtualScroller": {
              overflowX: "auto",
            },
            "& .ulb-reason-cell-wrap": {
              whiteSpace: "normal !important",
              wordBreak: "break-word",
              py: 1,
            },
          }}
          rowHeight={40}
          getRowHeight={() => "auto"}
          getRowId={getRequestRowId}
          rows={pageRequests}
          columns={columns}
          components={{ Footer: CustomFooter }}
          disableVirtualization
          disableSelectionOnClick
          getRowSpacing={(params) => ({
            top: params.isFirstVisible ? 0 : 3,
            bottom: params.isLastVisible ? 0 : 3,
          })}
          localeText={{ noRowsLabel: "No Budget Requests" }}
          loading={isTableLoading}
          checkboxSelection
          isRowSelectable={(params) => params.row.status === "pending"}
          onSelectionModelChange={(selection) => setSelectedRowIds(selection)}
          selectionModel={selectedRowIds}
        />
      )}

      {/* Approve/reject confirmation dialog, styled to match the
          UlbRequestModal/BudgetAtRiskModal convention (accent-bordered
          title, Box/Typography layout, rounded corners) instead of the
          older plain AdminConfirmationModal look. */}
      <Dialog
        open={reviewOpen}
        onClose={closeReview}
        maxWidth="xs"
        fullWidth
        TransitionProps={{ onExited: () => setReviewTarget(null) }}
        PaperProps={{ sx: { borderRadius: "12px", overflow: "hidden" } }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: `3px solid ${reviewTarget?.decision === "approve" ? ACCENT : USAGE_RED}`,
            py: 2,
            px: 3,
          }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: "#1a1a1a" }}>
            {reviewTarget?.decision === "approve"
              ? `Approve Budget Request${reviewTarget.requests.length > 1 ? "s" : ""}`
              : `Reject Budget Request${reviewTarget?.requests.length > 1 ? "s" : ""}`}
          </Typography>
          <IconButton size="small" onClick={closeReview} sx={{ color: "#555" }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 3, pb: 2.5, "&.MuiDialogContent-root": { pt: 1.5 } }}>
          {reviewTarget && (
            <>
              <Typography sx={{ fontSize: "0.85rem", color: "#1a1a1a", mb: reviewTarget.requests.length > 1 ? 1 : 2 }}>
                {reviewTarget.requests.length === 1 ? (
                  reviewTarget.decision === "approve" ? (
                    <>
                      This will {reviewTarget.requests[0].action} {reviewTarget.requests[0].email}'s budget by{" "}
                      <strong>{formatUsd(reviewTarget.requests[0].change_amount)}</strong>.
                    </>
                  ) : (
                    `Are you sure you want to reject ${reviewTarget.requests[0].email}'s request?`
                  )
                ) : reviewTarget.decision === "approve" ? (
                  `This will approve the following ${reviewTarget.requests.length} budget requests:`
                ) : (
                  `This will reject the following ${reviewTarget.requests.length} budget requests:`
                )}
              </Typography>
              {reviewTarget.requests.length > 1 && (
                <Box sx={{ maxHeight: "12rem", overflowY: "auto", mb: 2, pr: 0.5 }}>
                  {reviewTarget.requests.map((r) => {
                    const shared = isSharedScope(r.current_budget?.budget_scope);
                    return (
                      <Box
                        key={r.email}
                        sx={{ border: "1px solid #e8e8e8", borderRadius: "6px", p: 0.8, mb: 0.6, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1 }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
                          <Typography sx={{ fontSize: "0.78rem", color: "#1a1a1a" }}>{r.email}</Typography>
                          {shared && reviewTarget.decision === "approve" && (
                            <Tooltip title={`No ULB yet — approving creates one, split off from their current ${scopeLabel(r.current_budget?.budget_scope)}.`}>
                              <WarningAmberIcon sx={{ fontSize: "0.85rem", color: USAGE_ORANGE }} />
                            </Tooltip>
                          )}
                        </Box>
                        <Typography sx={{ fontSize: "0.78rem", fontWeight: 600, color: "#555" }}>
                          {formatUsd(r.change_amount)}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              )}
              {reviewTarget.decision === "approve" &&
                reviewTarget.requests.length === 1 &&
                isSharedScope(reviewTarget.requests[0].current_budget?.budget_scope) && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 1,
                      mb: 2,
                      p: 1.2,
                      backgroundColor: "#fff8e1",
                      border: "1px solid #ffe082",
                      borderRadius: "8px",
                    }}
                  >
                    <WarningAmberIcon sx={{ fontSize: "1.1rem", color: "#a17f00", mt: 0.2 }} />
                    <Typography sx={{ fontSize: "0.78rem", color: "#7a6100" }}>
                      {reviewTarget.requests[0].email} doesn't have a ULB yet — approving will create one for
                      them, split off from their current{" "}
                      {scopeLabel(reviewTarget.requests[0].current_budget?.budget_scope)}.
                    </Typography>
                  </Box>
                )}
              {reviewTarget.decision === "approve" && (
                <>
                  <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, color: "#333", mb: 0.7 }}>
                    Reason <span style={{ color: "#999", fontWeight: 400 }}>(optional)</span>
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    multiline
                    rows={2}
                    placeholder="Add context for this approval…"
                    value={approveReason}
                    onChange={(e) => setApproveReason(e.target.value)}
                    sx={{ "& .MuiInputBase-input": { fontSize: "0.82rem" } }}
                  />
                </>
              )}
              {reviewTarget.decision === "reject" && (
                <>
                  <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, color: "#333", mb: 0.7 }}>
                    Reason for Rejection <span style={{ color: USAGE_RED }}>*</span>
                  </Typography>
                  <Select
                    fullWidth
                    size="small"
                    displayEmpty
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    renderValue={(value) => value || <span style={{ color: "#999" }}>-- Select a reason --</span>}
                    sx={{ backgroundColor: "#fff", fontSize: "0.82rem", mb: rejectReason === "Other" ? 1.5 : 0 }}
                  >
                    {REJECT_REASON_OPTIONS.map((opt) => (
                      <MenuItem key={opt} value={opt}>
                        {opt}
                      </MenuItem>
                    ))}
                  </Select>
                  {rejectReason === "Other" && (
                    <TextField
                      fullWidth
                      size="small"
                      multiline
                      rows={2}
                      placeholder="Please specify"
                      value={otherReason}
                      onChange={(e) => setOtherReason(e.target.value)}
                      sx={{ "& .MuiInputBase-input": { fontSize: "0.82rem" } }}
                    />
                  )}
                </>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #f0f0f0" }}>
          <Button onClick={closeReview} sx={{ textTransform: "none", color: "#555" }}>
            Cancel
          </Button>
          <Button
            onClick={confirmReview}
            variant="contained"
            disabled={processing || rejectReasonInvalid}
            sx={{
              textTransform: "none",
              backgroundColor: reviewTarget?.decision === "approve" ? ACCENT : USAGE_RED,
              "&:hover": { backgroundColor: reviewTarget?.decision === "approve" ? "#1e6b0a" : "#b81d15" },
            }}
          >
            {processing ? (
              <CircularProgress size={18} sx={{ color: "#fff" }} />
            ) : reviewTarget?.decision === "approve" ? (
              "Approve"
            ) : (
              "Reject"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <UserDetailsModal
        open={showUserDetailsModal}
        onClose={() => {
          setShowUserDetailsModal(false);
          setSelectedUserDetails(null);
          setUserDetailsError("");
        }}
        user={selectedUserDetails}
        platformId={platformId}
        orgIdToName={orgIdToName}
        costCenterById={costCenterById}
        isLoading={isUserDetailsLoading}
        error={userDetailsError}
      />
    </Box>
  );
};

export default UlbRequestsTable;
